import { type ReactNode } from "react";
import {
  DecoratorNode,
  type DOMExportOutput,
  type LexicalNode,
  type NodeKey,
  type SerializedLexicalNode,
  type Spread,
} from "lexical";

const DIRECTIVE_NODE_TYPE = "directive-invocation";

export interface DirectiveInvocationPayload {
  name: string;
}

type SerializedDirectiveInvocationNode = Spread<
  DirectiveInvocationPayload,
  SerializedLexicalNode
>;

export class DirectiveInvocationNode extends DecoratorNode<ReactNode> {
  __payload: DirectiveInvocationPayload;

  static getType(): string {
    return DIRECTIVE_NODE_TYPE;
  }

  static clone(node: DirectiveInvocationNode): DirectiveInvocationNode {
    return new DirectiveInvocationNode(node.__payload, node.__key);
  }

  static importJSON(
    serializedNode: SerializedLexicalNode
  ): DirectiveInvocationNode {
    return $createDirectiveInvocationNode(
      serializedNode as SerializedDirectiveInvocationNode
    );
  }

  static importDOM() {
    return {};
  }

  constructor(payload: DirectiveInvocationPayload, key?: NodeKey) {
    super(key);
    this.__payload = payload;
  }

  createDOM(): HTMLElement {
    const span = document.createElement("span");
    span.className = "inline-flex align-middle";

    return span;
  }

  updateDOM(): false {
    return false;
  }

  exportDOM(): DOMExportOutput {
    const span = document.createElement("span");
    span.textContent = `/${this.__payload.name}`;

    return { element: span };
  }

  exportJSON(): SerializedDirectiveInvocationNode {
    return {
      ...super.exportJSON(),
      name: this.__payload.name,
    };
  }

  getTextContent(): string {
    return `/${this.__payload.name}`;
  }

  isInline(): true {
    return true;
  }

  isKeyboardSelectable(): true {
    return true;
  }

  decorate(): ReactNode {
    return <DirectiveInvocationPill payload={this.__payload} />;
  }

  getPayload(): DirectiveInvocationPayload {
    return this.getLatest().__payload;
  }
}

export const $createDirectiveInvocationNode = (
  payload: DirectiveInvocationPayload
): DirectiveInvocationNode => new DirectiveInvocationNode(payload);

export const $isDirectiveInvocationNode = (
  node: LexicalNode | null | undefined
): node is DirectiveInvocationNode => node instanceof DirectiveInvocationNode;

const DirectiveInvocationPill = ({
  payload,
}: {
  payload: DirectiveInvocationPayload;
}) => (
  <span
    className="mx-0.5 inline-flex max-w-full flex-wrap items-center gap-1 rounded border border-purple-200 bg-purple-50 px-1.5 py-1 text-sm align-middle text-purple-900"
    contentEditable={false}
    data-testid={`duck-directive-pill-${payload.name}`}
  >
    <span className="font-medium">/{payload.name}</span>
  </span>
);
